import { useState } from 'react';
import { Calendar, MapPin, Users, Wifi, Car, Wind, Waves, Star, Share2, CheckCircle, ChevronLeft, ChevronRight, Heart, Shield, Home } from 'lucide-react';

export default function PropertyPublicPage() {
  const [currentImage, setCurrentImage] = useState(0);
  const [liked, setLiked] = useState(false);
  const [copied, setCopied] = useState(false);

  const property: any = null;

  const copyLink = () => {
    navigator.clipboard.writeText('vecindus.com/p/santafe-apt');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!property) return (
    <div className="min-h-full bg-[var(--v-surface-1)] p-8"><div className="rounded-2xl border border-[var(--v-border-light)] bg-[var(--v-surface-1)] p-10 text-center">
            <p className="font-semibold text-[var(--v-text-primary)]">Sin datos todavía</p>
            <p className="mt-1 text-sm text-[var(--v-text-secondary)]">Esta sección se llenará con información real cuando registres propiedades y actividad.</p>
          </div></div>
  );

  return (
    <div className="min-h-full bg-[var(--v-surface-1)]">
      {/* Nav */}
      <nav className="bg-[var(--v-surface-1)] border-b border-[var(--v-surface-3)] px-4 py-3 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="text-white font-bold text-lg">Vecindus Rentals</span>
          </div>
          <button onClick={copyLink} className="text-slate-300 hover:text-white flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            {copied ? '¡Copiado!' : 'Compartir'}
            {copied && <CheckCircle className="w-4 h-4 text-green-400" />}
          </button>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Gallery */}
        <div className="relative rounded-2xl overflow-hidden mb-6">
          <div className="aspect-video bg-gradient-to-br from-slate-700 to-slate-600 flex items-center justify-center">
            <Home className="w-20 h-20 text-slate-500" />
          </div>
          <button onClick={() => setCurrentImage((p) => (p - 1 + 4) % 4)} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-[var(--v-surface-1)]/80 rounded-full flex items-center justify-center text-white">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button onClick={() => setCurrentImage((p) => (p + 1) % 4)} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-[var(--v-surface-1)]/80 rounded-full flex items-center justify-center text-white">
            <ChevronRight className="w-6 h-6" />
          </button>
          <button onClick={() => setLiked(!liked)} className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center">
            <Heart className={`w-5 h-5 ${liked ? 'fill-red-500 text-red-500' : 'text-slate-600'}`} />
          </button>
          <div className="absolute bottom-4 right-4 bg-[var(--v-surface-1)]/80 px-3 py-1 rounded-full text-white text-sm">
            {currentImage + 1} / {property.images}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Details */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[var(--v-text-primary)] mb-2">{property.title}</h1>
              <div className="flex items-center gap-4 text-slate-400">
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {property.location}
                </span>
                <span className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  <span className="text-white font-medium">{property.rating}</span>
                  <span>({property.reviews} reseñas)</span>
                </span>
              </div>
            </div>

            {/* Specs */}
            <div className="flex gap-6 py-4 border-y border-[var(--v-surface-3)]">
              <div className="text-center">
                <Users className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                <p className="text-white font-medium">{property.maxGuests}</p>
                <p className="text-slate-500 text-sm">Huéspedes</p>
              </div>
              <div className="text-center">
                <span className="text-2xl text-slate-400">🛏️</span>
                <p className="text-white font-medium">{property.bedrooms}</p>
                <p className="text-slate-500 text-sm">Habitaciones</p>
              </div>
              <div className="text-center">
                <span className="text-2xl text-slate-400">🚿</span>
                <p className="text-white font-medium">{property.bathrooms}</p>
                <p className="text-slate-500 text-sm">Baños</p>
              </div>
            </div>

            {/* Description */}
            <div>
              <h2 className="text-xl font-semibold text-[var(--v-text-primary)] mb-3">Acerca de este lugar</h2>
              <p className="text-slate-300 leading-relaxed">{property.description}</p>
            </div>

            {/* Amenities */}
            <div>
              <h2 className="text-xl font-semibold text-[var(--v-text-primary)] mb-3">Lo que ofrece</h2>
              <div className="grid grid-cols-2 gap-3">
                {property.amenities.map((a: any, i: number) => (
                  <div key={i} className="flex items-center gap-2 text-slate-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    {a}
                  </div>
                ))}
              </div>
            </div>

            {/* Calendar Preview */}
            <div>
              <h2 className="text-xl font-semibold text-[var(--v-text-primary)] mb-3">Disponibilidad</h2>
              <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)]">
                <div className="grid grid-cols-7 gap-2 text-center text-sm">
                  {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(d => (
                    <span key={d} className="text-slate-500">{d}</span>
                  ))}
                  {Array.from({length: 31}, (_, i) => i + 1).map(d => (
                    <button
                      key={d}
                      disabled={[20, 21, 22, 23, 24, 28, 29, 30, 31].includes(d)}
                      className={`py-2 rounded-lg ${
                        [20, 21, 22, 23, 24, 28, 29, 30, 31].includes(d)
                          ? 'bg-red-500/20 text-red-400 cursor-not-allowed'
                          : 'hover:bg-slate-700 text-slate-300'
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-4 mt-4 text-sm">
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-slate-700 rounded" />
                    <span className="text-slate-400">Disponible</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-red-500/20 rounded" />
                    <span className="text-slate-400">Ocupado</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Booking Card */}
          <div className="lg:col-span-1">
            <div className="bg-[var(--v-surface-1)] p-6 rounded-xl border border-[var(--v-surface-3)] sticky top-24">
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-3xl font-bold text-white">${property.pricePerNight}</span>
                <span className="text-slate-400">/noche</span>
              </div>

              <div className="space-y-3 mb-4">
                <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium">
                  Reservar Ahora
                </button>
                <button className="w-full bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Ver Calendario Completo
                </button>
              </div>

              <div className="text-center text-slate-500 text-sm">
                <p>Cancelación gratuita hasta 48h antes</p>
                <p className="mt-1">Sin cargos por servicio</p>
              </div>

              <div className="mt-6 pt-6 border-t border-[var(--v-surface-3)]">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                    M
                  </div>
                  <div>
                    <p className="text-white font-medium">Anfitrión: María</p>
                    <p className="text-slate-400 text-sm">Superanfitrión</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3 text-sm text-slate-400">
                  <Shield className="w-4 h-4 text-green-400" />
                  Verificado por Vecindus
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-12 py-8 border-t border-[var(--v-surface-3)] text-center text-slate-500">
          <p>Powered by <span className="text-blue-400 font-medium">Vecindus Rentals</span></p>
          <p className="text-sm mt-2">Reserva directa • Sin comisiones • Soporte local</p>
        </footer>
      </div>
    </div>
  );
}
